import { useEffect } from "react";
import { api } from "@/lib/api";

// Convertit un hex #RRGGBB en triplet HSL "H S% L%" (pour les variables Tailwind)
function hexToHslParts(hex) {
  let h = (hex || "").replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  if ([r, g, b].some((x) => Number.isNaN(x))) return null;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hue = 0, sat = 0; const li = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    sat = li > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) hue = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) hue = (b - r) / d + 2;
    else hue = (r - g) / d + 4;
    hue /= 6;
  }
  return `${Math.round(hue * 360)} ${Math.round(sat * 100)}% ${Math.round(li * 100)}%`;
}

function darken(hex, amount = 0.14) {
  let h = (hex || "").replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const num = parseInt(h, 16);
  let r = (num >> 16) & 255, g = (num >> 8) & 255, b = num & 255;
  r = Math.max(0, Math.round(r * (1 - amount)));
  g = Math.max(0, Math.round(g * (1 - amount)));
  b = Math.max(0, Math.round(b * (1 - amount)));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

function lighten(hex, amount = 0.12) {
  let h = (hex || "").replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const num = parseInt(h, 16);
  let r = (num >> 16) & 255, g = (num >> 8) & 255, b = num & 255;
  r = Math.min(255, Math.round(r + (255 - r) * amount));
  g = Math.min(255, Math.round(g + (255 - g) * amount));
  b = Math.min(255, Math.round(b + (255 - b) * amount));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

// Applique un thème global AU SITE ENTIER : variables CSS + surcharge des
// classes utilitaires qui utilisent le hex de marque littéral (#E05638 / #c94a2f).
export function applyTheme(theme) {
  const primary = theme?.primary || "#E05638";
  const hsl = hexToHslParts(primary);
  const hover = darken(primary, 0.14);
  const light = lighten(primary, 0.1);
  const headingFont = theme?.headingFont || "Outfit";
  const bodyFont = theme?.bodyFont || "DM Sans";

  // Charge les polices Google si nécessaire
  const fontId = "cms-theme-fonts";
  const families = [headingFont, bodyFont].filter(Boolean).map((f) => f.replace(/ /g, "+") + ":wght@400;500;600;700;800").join("&family=");
  let link = document.getElementById(fontId);
  if (!link) { link = document.createElement("link"); link.id = fontId; link.rel = "stylesheet"; document.head.appendChild(link); }
  link.href = `https://fonts.googleapis.com/css2?family=${families}&display=swap`;

  const E = "#E05638", H = "#c94a2f"; // couleurs de marque d'origine à remapper
  const esc = "\\[\\#"; // helper lisibilité
  const css = `
:root { ${hsl ? `--primary: ${hsl}; --ring: ${hsl};` : ""} }
.text-${esc}E05638\\] { color: ${primary} !important; }
.hover\\:text-${esc}E05638\\]:hover { color: ${primary} !important; }
.bg-${esc}E05638\\] { background-color: ${primary} !important; }
.hover\\:bg-${esc}E05638\\]:hover { background-color: ${primary} !important; }
.hover\\:bg-${esc}c94a2f\\]:hover { background-color: ${hover} !important; }
.border-${esc}E05638\\] { border-color: ${primary} !important; }
.ring-${esc}E05638\\] { --tw-ring-color: ${primary} !important; }
.fill-${esc}E05638\\] { fill: ${primary} !important; }
.stroke-${esc}E05638\\] { stroke: ${primary} !important; }
.decoration-${esc}E05638\\] { text-decoration-color: ${primary} !important; }
.btn-shine { background-image: linear-gradient(135deg, ${primary} 0%, ${light} 100%) !important; }
.cms-rich a, .prose-proxi a { color: ${primary}; }
body { font-family: '${bodyFont}', system-ui, -apple-system, sans-serif; }
h1,h2,h3,h4,h5,h6,.font-heading { font-family: '${headingFont}', system-ui, sans-serif; }
`;
  const id = "cms-theme-overrides";
  let style = document.getElementById(id);
  if (!style) { style = document.createElement("style"); style.id = id; document.head.appendChild(style); }
  style.textContent = css;
  void E; void H;
}

// Composant monté une fois au niveau racine : récupère le thème publié et l'applique.
export default function SiteTheme() {
  useEffect(() => {
    api.get("/public/cms/settings/theme").then(({ data }) => applyTheme(data)).catch(() => {});
  }, []);
  return null;
}
